import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { leadsAPI, usersAPI, projectsAPI, settingsAPI } from '../services/api';
import { useToast } from './Toast';
import DataTransferModal from './DataTransferModal';
import LeadStatsBar from './LeadStatsBar';
import { formatCurrency, getStatusColor, formatStatusLabel, formatPhoneDisplay, formatLocation, getSourceBadgeColor } from '../utils/formatters';
import {
  Plus, X, Download, Upload, Eye, MoreHorizontal,
  Trash2, Users as UsersIcon,
  ChevronDown, History, MessageSquare, FileText, Clock,
  Activity, Phone, Mail
} from 'lucide-react';
import LeadFormDrawer from './LeadFormDrawer';
import Pagination from './Pagination';
import LeadSearchBar from './LeadSearchBar';
import { saveLeadsState, loadLeadsState, clearLeadsState } from '../utils/leadsListState';

function getScrollContainer() {
  return typeof document !== 'undefined' ? document.querySelector('main') || window : null;
}

const avatarColors = [
  { bg: "#EEEDFE", fg: "#3C3489" },
  { bg: "#E1F5EE", fg: "#085041" },
  { bg: "#FAEEDA", fg: "#633806" },
  { bg: "#FAECE7", fg: "#712B13" },
  { bg: "#E6F1FB", fg: "#0C447C" },
  { bg: "#FBEAF0", fg: "#72243E" },
  { bg: "#EAF3DE", fg: "#27500A" },
  { bg: "#F1EFE8", fg: "#444441" },
];

function getAvatarColor(name) {
  let hash = 0;
  for (const char of name) hash = (hash + char.charCodeAt(0)) % avatarColors.length;
  return avatarColors[hash];
}

function getInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(" ");
  return (parts[0][0] + (parts[1] ? parts[1][0] : "")).toUpperCase();
}

const PAGE_SIZE = 50;
const DEFAULT_LEAD_VIEWS = [
  { key: 'general', label: 'General Leads', mode: 'general', show: ['name', 'phone', 'project', 'property_type', 'budget', 'status', 'last_action', 'created_at', 'assigned_to', 'source'] },
  { key: 'meta_ads', label: 'Meta Ads', mode: 'meta_ads', show: ['name', 'phone', 'project', 'status', 'last_action', 'created_at', 'assigned_to', 'source'] },
  { key: 'eighteen', label: 'Eighteen', mode: 'project', project_hint: 'eighteen', show: ['purchaser_name', 'phone', 'email', 'status', 'last_action', 'created_at', 'assigned_to', 'source'] }
];

const Leads = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [dataTransferState, setDataTransferState] = useState({ open: false, mode: 'import', source: null });
  const [showImportHistory, setShowImportHistory] = useState(false);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [importHistory, setImportHistory] = useState([]);
  const [importHistoryLoading, setImportHistoryLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSource, setFilterSource] = useState('General');
  const [filterIntent, setFilterIntent] = useState('');
  const [filterProject, setFilterProject] = useState('all');
  const [showMobileActions, setShowMobileActions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [hasRestoredLeadPosition, setHasRestoredLeadPosition] = useState(false);
  const [highlightedLeadId, setHighlightedLeadId] = useState(null);
  const [savedScrollY, setSavedScrollY] = useState(null);
  const [savedReturnRowId, setSavedReturnRowId] = useState(null);

  const navigateToLeadDetail = (leadId) => {
    const returnTo = `${location.pathname}${location.search}` || '/leads';
    const container = getScrollContainer();
    const returnScroll = container ? container.scrollTop : 0;
    const returnLeadState = {
      activeLeadView,
      filterSource,
      filterIntent,
      filterProject,
      filterStatus,
      searchTerm,
      page,
      returnRowId: leadId
    };
    saveLeadsState({ ...returnLeadState, scrollY: returnScroll });
    navigate(`/leads/${leadId}`, { state: { returnTo, returnScroll, returnLeadState } });
  };

  const [selectedLeads, setSelectedLeads] = useState([]);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [bulkAssignAgent, setBulkAssignAgent] = useState('');
  const [leadViews, setLeadViews] = useState(DEFAULT_LEAD_VIEWS);
  const [activeLeadView, setActiveLeadView] = useState('general');
  const [rowRules, setRowRules] = useState({ stale_after_hours: 72, due_soon_hours: 24, show_priority_chip: true, show_last_touch_chip: true });
  const assignableUsers = users.filter((u) => String(u?.role || '').trim().toLowerCase() === 'sales_agent' && u?.is_active !== false);
  const getAgentLabel = (u) => {
    const name = (u?.full_name && String(u.full_name).trim()) ? String(u.full_name).trim() : (u?.username || 'Unknown');
    return `${name} (${u.new_leads_count || 0} New)`;
  };
  
  const [propertyTypes, setPropertyTypes] = useState(['Rent', 'Buy', 'Sell']);
  const [statusOptions, setStatusOptions] = useState(['new', 'contacted', 'in_progress', 'follow_up', 'negotiating', 'won', 'lost', 'queued_unassigned', 'archived']);
  const [intentOptions, setIntentOptions] = useState(['Buy', 'Sell', 'Rent']);
  const [sources, setSources] = useState(['General', 'Meta Ads', 'Eighteen', 'Referral', 'Whatsapp referral', 'Website', 'Walk-in', 'Phone Call']);

  const maskPhone = (phone) => {
    if (!phone) return '-';
    const digits = String(phone).replace(/\D/g, '');
    if (digits.length >= 10) {
      return digits.slice(0, 4) + '-xxxx-' + digits.slice(-2);
    }
    return 'xxxx-xxxx';
  };

  const formatBudgetDisplay = (budget) => {
    if (!budget || isNaN(Number(budget))) return '-';
    const num = Number(budget);
    if (num >= 10000000) return `PKR ${(num / 10000000).toFixed(1).replace(/\.0$/, '')} Cr`;
    if (num >= 100000) return `PKR ${(num / 100000).toFixed(1).replace(/\.0$/, '')} Lac`;
    return formatCurrency(num);
  };

  const timeAgo = (date) => {
    if (!date) return '';
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + 'y ago';
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + 'mo ago';
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + 'd ago';
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + 'h ago';
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + 'm ago';
    return 'Just now';
  };

  const renderLastAction = (lead) => {
    let Icon = Activity;
    let actionText = 'Updated';
    let lastActionAt = lead.last_activity_date || lead.updated_at || lead.created_at;

    if (lead.last_event_type) {
      lastActionAt = lead.last_event_date || lastActionAt;
      const evt = String(lead.last_event_type).toLowerCase();
      
      if (evt.includes('call')) {
        Icon = Phone;
        actionText = 'Call';
      } else if (evt.includes('whatsapp') || evt.includes('message') || evt.includes('email')) {
        Icon = MessageSquare;
        actionText = 'Message';
      } else if (evt.includes('note')) {
        Icon = FileText;
        actionText = 'Note';
      } else if (evt.includes('status')) {
        Icon = Activity;
        actionText = 'Status';
      } else {
        Icon = Clock;
        actionText = 'Activity';
      }
    } else {
      // Fallback to legacy parsing if no timeline events
      const noteText = String(lead?.notes || '').toLowerCase();
      const sourceText = String(lead?.source || '').toLowerCase();
      if (noteText.includes('whatsapp') || sourceText.includes('whatsapp')) {
        Icon = MessageSquare;
        actionText = 'WhatsApp';
      } else if (noteText.includes('call')) {
        Icon = Phone;
        actionText = 'Call';
      } else if (lead.notes) {
        Icon = FileText;
        actionText = 'Note';
      }
    }

    return (
      <div className="flex items-center gap-1.5 text-gray-500">
        <Icon size={14} />
        <span className="text-sm">{actionText} {timeAgo(lastActionAt)}</span>
      </div>
    );
  };

  useEffect(() => {
    isRestoringRef.current = true;
    const savedState = loadLeadsState();
    const routeState = location.state?.restoreLeadState;
    const restoreState = routeState || savedState;

    const search = restoreState?.searchTerm ?? (searchParams.get('search') || '');
    const status = restoreState?.filterStatus ?? (searchParams.get('status') || 'all');
    const source = restoreState?.filterSource ?? 'General';
    const intent = restoreState?.filterIntent ?? '';
    const projectId = restoreState?.filterProject ?? 'all';
    const viewKey = restoreState?.activeLeadView ?? 'general';
    const pageToLoad = restoreState?.page || 1;

    const scrollY = location.state?.restoreScroll ?? savedState?.scrollY;
    console.log('[Restore] scrollY:', scrollY, 'returnRowId:', restoreState?.returnRowId, 'page:', pageToLoad, 'routeState:', !!routeState, 'savedState:', !!savedState);
    setSavedScrollY(scrollY);
    setSavedReturnRowId(restoreState?.returnRowId || null);

    if (restoreState?.returnRowId) {
      setHighlightedLeadId(restoreState.returnRowId);
    }

    setSearchTerm(search);
    setFilterStatus(status);
    setFilterSource(source);
    setFilterIntent(intent);
    setFilterProject(projectId);
    setActiveLeadView(viewKey);
    setPage(pageToLoad);

    loadLeads(pageToLoad, search, status, source, intent, projectId);

    if (routeState || savedState) {
      clearLeadsState();
    }

    setTimeout(() => { isRestoringRef.current = false; }, 0);
  }, []);

  useEffect(() => {
    if (hasRestoredLeadPosition) return;
    const restoreScroll = savedScrollY ?? location.state?.restoreScroll;
    if (restoreScroll == null) return;

    if (leads.length === 0) return;

    requestAnimationFrame(() => {
      const container = getScrollContainer();
      if (container) container.scrollTop = Number(restoreScroll) || 0;
      setHasRestoredLeadPosition(true);
    });
  }, [leads, hasRestoredLeadPosition, savedScrollY, location.state]);

  useEffect(() => {
    if (!highlightedLeadId) return;
    const row = document.getElementById(`lead-row-${highlightedLeadId}`);
    if (!row) return;
    row.classList.add('bg-yellow-100');
    row.classList.remove('bg-[#F9FAFB]', 'bg-white', 'bg-gray-50');
    const timer = setTimeout(() => {
      row.classList.remove('bg-yellow-100');
      setHighlightedLeadId(null);
    }, 2000);
    return () => clearTimeout(timer);
  }, [highlightedLeadId, leads]);

  useEffect(() => {
    const user = localStorage.getItem('user');
    if (user) {
      const userData = JSON.parse(user);
      setUserRole(userData.role);
    }
    loadUsers();
    loadProjects();
    loadLeadWorkspaceConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLeadWorkspaceConfig = async () => {
    try {
      const response = await settingsAPI.getPublic();
      const cfgViews = Array.isArray(response?.data?.leadViewsConfig) ? response.data.leadViewsConfig : null;
      if (cfgViews && cfgViews.length > 0) setLeadViews(cfgViews);
      const cfgRules = response?.data?.leadRowIntelligenceConfig;
      if (cfgRules && typeof cfgRules === 'object') setRowRules(cfgRules);

      const features = response?.data?.features || {};
      if (Array.isArray(features.lead_statuses) && features.lead_statuses.length > 0) setStatusOptions(features.lead_statuses);
      if (Array.isArray(features.lead_sources) && features.lead_sources.length > 0) setSources(features.lead_sources);
      if (Array.isArray(features.lead_intents) && features.lead_intents.length > 0) setIntentOptions(features.lead_intents);
      if (Array.isArray(features.lead_property_types) && features.lead_property_types.length > 0) setPropertyTypes(features.lead_property_types);
    } catch (error) {
      // silently use defaults
    }
  };

  const loadLeads = useCallback(async (pageNum = page, search = searchTerm, status = filterStatus, source = filterSource, intent = filterIntent, projectId = filterProject) => {
    try {
      setLoading(true);
      const params = {
        page: pageNum,
        limit: PAGE_SIZE,
        search: search || undefined,
        status: status && status !== 'all' ? status : undefined,
        source: source && source !== 'all' ? source : undefined,
        intent: intent && intent !== 'all' ? intent : undefined,
        project_id: projectId && projectId !== 'all' ? projectId : undefined
      };
      const response = await leadsAPI.getAll(params);
      const data = response.data;
      if (Array.isArray(data)) {
        setLeads(data);
        setTotal(data.length);
        setPage(1);
        setTotalPages(1);
      } else {
        setLeads(data.leads || []);
        setTotal(data.total ?? 0);
        setPage(data.page ?? 1);
        setTotalPages(data.totalPages ?? 1);
      }
    } catch (error) {
      console.error('Failed to load leads:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to load leads';
      showToast(errorMsg, 'error');
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, filterStatus, filterSource, filterIntent, filterProject, showToast]);

  const formatShortDate = (value) => {
    if (!value) return '-';
    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) return value;
    return dt.toLocaleDateString('en-GB');
  };
  const getLeadScoreMeta = (score) => {
    const s = Number(score || 0);
    if (s >= 70) return { bars: 3, badge: 'bg-green-100 text-green-700 border border-green-200' };
    if (s >= 40) return { bars: 2, badge: 'bg-amber-100 text-amber-700 border border-amber-200' };
    return { bars: 1, badge: 'bg-red-100 text-red-700 border border-red-200' };
  };
  const renderMobileLeadTitle = (lead) => {
    if (activeLeadView === 'eighteen') return lead.purchaser_name || lead.name || 'Lead';
    return lead.name || 'Lead';
  };

  const leadEmptyCols = 25;

  const filterChangeRef = useRef(false);
  const isRestoringRef = useRef(false);
  const headerMenuRef = useRef(null);

  useEffect(() => {
    const onDocClick = (event) => {
      if (!headerMenuRef.current) return;
      if (!headerMenuRef.current.contains(event.target)) setShowHeaderMenu(false);
    };
    if (showHeaderMenu) document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showHeaderMenu]);
  useEffect(() => {
    if (!filterChangeRef.current) {
      filterChangeRef.current = true;
      return;
    }
    if (isRestoringRef.current) {
      isRestoringRef.current = false;
      return;
    }
    setPage(1);
    const params = {};
    if (searchTerm) params.search = searchTerm;
    if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
    if (filterSource && filterSource !== 'all') params.source = filterSource;
    if (filterIntent && filterIntent !== 'all') params.intent = filterIntent;
    if (filterProject && filterProject !== 'all') params.project_id = filterProject;
    setSearchParams(params, { replace: true });

    const t = setTimeout(() => {
      loadLeads(1, searchTerm, filterStatus, filterSource, filterIntent, filterProject);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, filterStatus, filterSource, filterIntent, filterProject]);

  const loadUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to load users:', error);
      // Don't throw - just log the error and continue with empty users
      setUsers([]);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await projectsAPI.getAll();
      setProjects(response.data || []);
    } catch (error) {
      console.error('Failed to load projects:', error);
      setProjects([]);
    }
  };



  const getAssignableLeads = () => leads.filter(l => !l.assigned_to_id && !l.assigned_to);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedLeads(getAssignableLeads().map(l => l.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (id, isAssigned) => {
    if (isAssigned) return; // Prevent selecting already assigned leads
    setSelectedLeads(prev => 
      prev.includes(id) ? prev.filter(lId => lId !== id) : [...prev, id]
    );
  };

  const handleBulkAssign = async () => {
    if (!bulkAssignAgent) {
      showToast('Please select an agent', 'error');
      return;
    }
    try {
      await leadsAPI.bulkUpdate({ lead_ids: selectedLeads, updates: { assigned_to_id: bulkAssignAgent } });
      showToast(`Successfully assigned ${selectedLeads.length} leads`, 'success');
      setSelectedLeads([]);
      setShowBulkAssignModal(false);
      setBulkAssignAgent('');
      loadLeads(page, searchTerm, filterStatus, filterSource);
    } catch (error) {
      showToast(error.response?.data?.error || 'Failed to bulk assign leads', 'error');
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;

    try {
      await leadsAPI.delete(leadId);
      loadLeads(page, searchTerm, filterStatus, filterSource);
      showToast('Lead deleted successfully', 'success');
    } catch (error) {
      console.error('Failed to delete lead:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to delete lead';
      showToast(errorMsg, 'error');
    }
  };

  const deriveEighteenProjectId = () => {
    const p = projects.find((proj) => String(proj?.name || '').toLowerCase().includes('eighteen'));
    return p?.id || null;
  };

  const applyLeadView = (viewKey) => {
    const view = leadViews.find((v) => v.key === viewKey) || leadViews[0];
    if (!view) return;
    setActiveLeadView(view.key);
    if (view.key === 'meta_ads') {
      setFilterSource('Meta Ads');
      setFilterProject('all');
      setFilterIntent('');
      return;
    }
    if (view.key === 'eighteen') {
      setFilterSource('Eighteen');
      setFilterProject('all');
      setFilterIntent('');
      return;
    }
    // general
    setFilterSource('General');
    setFilterProject('all');
    setFilterIntent('');
  };

  const goToPage = (nextPage) => {
    const p = Math.max(1, Math.min(nextPage, totalPages));
    setPage(p);
    loadLeads(p, searchTerm, filterStatus, filterSource);
  };

  const loadImportHistory = async () => {
    setImportHistoryLoading(true);
    try {
      const res = await leadsAPI.getImportHistory({ page: 1, limit: 20 });
      setImportHistory(res.data?.history || []);
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to load import history', 'error');
    } finally {
      setImportHistoryLoading(false);
    }
  };



  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto space-y-4">
        <div className="surface-glass rounded-2xl p-6 border border-slate-200">
          <div className="skeleton-shimmer h-8 w-56 rounded-md mb-3" />
          <div className="skeleton-shimmer h-4 w-80 rounded-md mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="skeleton-shimmer h-20 rounded-xl" />
            <div className="skeleton-shimmer h-20 rounded-xl" />
            <div className="skeleton-shimmer h-20 rounded-xl" />
          </div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((r) => (
              <div key={r} className="skeleton-shimmer h-12 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-5 max-w-7xl mx-auto overflow-x-hidden">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">Leads</h1>
        <div className="flex items-center gap-1.5">
          {/* Eighteen leads are import-only, no manual create */}
          {activeLeadView !== 'eighteen' ? (
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-[#111827] border border-[#111827] text-white hover:bg-black px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-colors"
            >
              <Plus size={16} />
              New lead
            </button>
          ) : userRole === 'admin' ? (
            <button
              onClick={() => setDataTransferState({ open: true, mode: 'import', source: 'eighteen' })}
              className="bg-[#111827] border border-[#111827] text-white hover:bg-black px-3 py-1.5 rounded-lg flex items-center gap-1.5 text-sm font-medium transition-colors"
            >
              <Upload size={16} />
              Import leads
            </button>
          ) : null}
          {userRole === 'admin' && (
            <div className="relative" ref={headerMenuRef}>
              <button
                onClick={() => setShowHeaderMenu((v) => !v)}
                className="p-1.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
                title="More actions"
              >
                <MoreHorizontal size={16} />
              </button>
              {showHeaderMenu && (
                <div className="absolute right-0 mt-2 w-44 rounded-lg border border-gray-200 bg-white shadow-md z-20 py-1">
                  <button
                    onClick={() => { setShowHeaderMenu(false); setDataTransferState({ open: true, mode: 'import', source: null }); }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Upload size={14} /> Import
                  </button>
                  <button
                    onClick={() => { setShowHeaderMenu(false); setDataTransferState({ open: true, mode: 'export', source: null }); }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <Download size={14} /> Export
                  </button>
                  <button
                    onClick={() => { setShowHeaderMenu(false); setShowImportHistory(true); loadImportHistory(); }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                  >
                    <History size={14} /> Import History
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* STATS BAR */}
      <div className="mb-8">
        <LeadStatsBar activeLeadView={activeLeadView} />
      </div>

      <div className="inline-flex flex-wrap gap-5 border-b border-gray-200 pb-1">
        {leadViews.map((view) => (
          <button
            key={view.key}
            onClick={() => applyLeadView(view.key)}
            className={`pb-2 text-sm transition-colors border-b-2 ${
              activeLeadView === view.key ? 'text-gray-900 border-b-gray-900 font-semibold' : 'text-slate-500 border-b-transparent hover:text-gray-700 font-medium'
            }`}
          >
            {view.label}
          </button>
        ))}
      </div>

      {/* FILTER ROW */}
      <div className="flex flex-col lg:flex-row gap-3 lg:gap-2.5 lg:items-center">
        <div className="relative flex-1">
          <LeadSearchBar 
            initialValue={searchTerm}
            onSearchTermChange={(term) => setSearchTerm(term)}
            onResultSelect={(lead) => navigateToLeadDetail(lead.id)} 
          />
        </div>
        <div className="flex flex-wrap gap-3">
          {[
            {
              value: filterStatus,
              onChange: (e) => setFilterStatus(e.target.value),
              options: [
                { value: 'all', label: 'All Status' },
                ...statusOptions.map((s) => ({ value: s, label: formatStatusLabel(s) })),
              ],
              key: 'status'
            },
            ...(activeLeadView === 'general' ? [{
              value: filterIntent,
              onChange: (e) => setFilterIntent(e.target.value),
              options: [
                { value: '', label: 'All Intents' },
                ...intentOptions.map((i) => ({ value: i, label: i })),
              ],
              key: 'intent'
            }] : []),
            {
              value: filterProject,
              onChange: (e) => setFilterProject(e.target.value),
              options: [
                { value: 'all', label: 'All Projects' },
                { value: 'general', label: 'General' },
                ...projects.map((p) => ({ value: p.id, label: p.name })),
              ],
              key: 'project'
            },
          ].map((cfg) => (
            <div key={cfg.key} className="relative min-w-[140px]">
              <select
                value={cfg.value}
                onChange={cfg.onChange}
                className="h-10 sm:h-9 w-full appearance-none bg-white border border-gray-200 rounded-md px-3 sm:px-3 pr-8 text-sm text-gray-600 cursor-pointer focus:outline-none focus:ring-0 focus:border-gray-200"
              >
                {cfg.options.map((opt) => (
                  <option key={`${cfg.key}-${String(opt.value)}`} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          ))}
        </div>
      </div>

      {/* MOBILE LIST */}
      <div className="md:hidden space-y-4">
        {leads.map((lead) => {
          const statusClasses = getStatusColor(lead.status);
          const score = Number(lead.lead_score || 0);
          const scoreMeta = getLeadScoreMeta(score);
          const phoneValue = lead.phone || lead.contact_number || '';
          return (
            <div id={`lead-row-${lead.id}`} key={`m-${lead.id}`} className="bg-white rounded-2xl border border-slate-200 shadow-sm px-4 py-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{renderMobileLeadTitle(lead)}</p>
                  <p className="text-sm text-gray-600 truncate">
                    {userRole === 'admin' ? formatPhoneDisplay(phoneValue) : maskPhone(phoneValue)}
                  </p>
                  {activeLeadView === 'eighteen' && (
                    <p className="text-xs text-gray-500 truncate">
                      {lead.unit_category || '-'} · {lead.unit_type || '-'}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => { navigateToLeadDetail(lead.id); }}
                  className="inline-flex items-center gap-1 rounded-full border border-blue-100 bg-white px-2.5 py-1 text-xs text-blue-700"
                  title="View Lead"
                >
                  <Eye size={14} />
                  View
                </button>
              </div>

              <div className="mt-3 flex items-center gap-2">
                <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusClasses}`}>
                  {formatStatusLabel(lead.status)}
                </span>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold ${scoreMeta.badge}`}>
                  <span className={`inline-block w-1 h-2 rounded-sm ${scoreMeta.bars >= 1 ? 'bg-current' : 'bg-current/30'}`} />
                  <span className={`inline-block w-1 h-3 rounded-sm ${scoreMeta.bars >= 2 ? 'bg-current' : 'bg-current/30'}`} />
                  <span className={`inline-block w-1 h-4 rounded-sm ${scoreMeta.bars >= 3 ? 'bg-current' : 'bg-current/30'}`} />
                </span>
              </div>

              <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                <span>{timeAgo(lead.last_activity_date || lead.updated_at || lead.created_at)}</span>
                <span>{new Date(lead.created_at).toLocaleDateString('en-GB')}</span>
              </div>
            </div>
          );
        })}
        {leads.length === 0 && !loading && (
          <div className="surface-glass rounded-xl border border-slate-200 p-6 text-center">
            <UsersIcon className="mx-auto h-10 w-10 text-gray-300" />
            <p className="mt-2 text-sm font-semibold text-gray-900">No leads found</p>
            <p className="text-sm text-gray-500">Try adjusting filters.</p>
          </div>
        )}
      </div>

      {/* TABLE */}
      <div className="mt-4 border-t border-gray-200 pt-0">
        <div className="mt-3 bg-white border border-gray-200 overflow-hidden">
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left border-collapse font-sans" style={{ fontFamily: 'Inter, sans-serif' }}>
              <thead className="bg-[#F9FAFB] border-b border-gray-200">
              <tr>
                {userRole === 'admin' && activeLeadView !== 'eighteen' && (
                  <th className="px-6 py-2.5 w-12">
                    <input 
                      type="checkbox" 
                      disabled={getAssignableLeads().length === 0}
                      checked={getAssignableLeads().length > 0 && selectedLeads.length === getAssignableLeads().length} 
                      ref={input => { if (input) input.indeterminate = selectedLeads.length > 0 && selectedLeads.length < getAssignableLeads().length; }}
                      onChange={handleSelectAll} 
                      className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                  </th>
                )}
                {activeLeadView === 'eighteen' ? (
                  <>
                    <th className="px-6 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 sticky left-0 z-10 bg-[#F9FAFB]">Purchaser Name</th>
                    <th className="px-6 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Contact Number</th>
                    <th className="px-6 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Email Address</th>
                  </>
                ) : (
                  <>
                    <th className="px-6 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 sticky left-0 z-10 bg-[#F9FAFB]">Lead Name</th>
                    <th className="px-6 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Contact</th>
                  </>
                )}
                {(activeLeadView === 'meta_ads' || activeLeadView === 'general') && (
                  <th className="px-6 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Project</th>
                )}
                {activeLeadView === 'general' && (
                  <>
                    <th className="px-6 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Intent</th>
                    <th className="px-6 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Budget</th>
                  </>
                )}
                <th className="px-6 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Status</th>
                <th className="px-6 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Lead Score</th>
                <th className="px-6 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Last Action</th>
                <th className="px-6 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Date Added</th>
                <th className="px-6 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Assigned Agent</th>
                <th className="px-6 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Lead Source</th>
                <th className="px-6 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-transparent">
              {leads.map((lead, index) => {
                let statusClasses = "bg-gray-50 text-gray-700";
                const s = lead.status?.toLowerCase();
                if (s === 'new') statusClasses = "bg-blue-50 text-blue-700";
                else if (s === 'contacted') statusClasses = "bg-yellow-50 text-yellow-700";
                else if (s === 'won') statusClasses = "bg-green-50 text-green-700";
                else if (s === 'lost') statusClasses = "bg-red-50 text-red-700";

                const isEven = index % 2 !== 0; // 0-indexed, so 1 is even row visually
                const isAssigned = !!lead.assigned_to_id;
                const assignedAgentLabel = lead.assigned_to_id
                  ? (lead.assigned_to_name || lead.assigned_to || 'Unknown Agent')
                  : 'Unassigned';

                return (
                  <tr id={`lead-row-${lead.id}`} key={lead.id} onClick={() => navigateToLeadDetail(lead.id)} className={`group relative h-[48px] cursor-pointer ${selectedLeads.includes(lead.id) ? 'bg-gray-50' : (isEven ? 'bg-[#F9FAFB]' : 'bg-white')} hover:bg-gray-50 transition-colors`}>
                    {userRole === 'admin' && activeLeadView !== 'eighteen' && (
                      <td className="px-6 py-2 whitespace-nowrap w-12">
                        <input 
                          type="checkbox" 
                          disabled={isAssigned}
                          checked={selectedLeads.includes(lead.id)}
                          onChange={(e) => { e.stopPropagation(); handleSelectLead(lead.id, isAssigned); }}
                          className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900 cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                          title={isAssigned ? "Already assigned" : "Select to assign"}
                        />
                      </td>
                    )}
                    {activeLeadView === 'eighteen' ? (
                      <>
                        <td className="px-6 py-2 whitespace-nowrap sticky left-0 z-10" style={{ backgroundColor: selectedLeads.includes(lead.id) ? '#F9FAFB' : isEven ? '#F9FAFB' : '#fff' }}>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0" style={{ backgroundColor: getAvatarColor(lead.purchaser_name || lead.name || '').bg, color: getAvatarColor(lead.purchaser_name || lead.name || '').fg }}>
                              {getInitials(lead.purchaser_name || lead.name || '')}
                            </div>
                            <div className="min-w-0">
                              <div className="font-semibold text-gray-900 text-[12px] truncate">{lead.purchaser_name || lead.name || '-'}</div>
                              <div className="text-[10px] text-gray-500 mt-0.5">{timeAgo(lead.last_activity_date || lead.updated_at || lead.created_at)}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-2 whitespace-nowrap">
                          <div className="text-gray-700 text-sm font-medium">
                            {userRole === 'admin'
                              ? formatPhoneDisplay(lead.phone || lead.contact_number)
                              : maskPhone(lead.phone || lead.contact_number)}
                          </div>
                        </td>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700">{lead.email || '-'}</td>
                      </>
                    ) : (
                      <>
                        <td className="px-6 py-2 whitespace-nowrap sticky left-0 z-10" style={{ backgroundColor: selectedLeads.includes(lead.id) ? '#F9FAFB' : isEven ? '#F9FAFB' : '#fff' }}>
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0" style={{ backgroundColor: getAvatarColor(lead.name || lead.purchaser_name || lead.customer_name || '').bg, color: getAvatarColor(lead.name || lead.purchaser_name || lead.customer_name || '').fg }}>
                              {getInitials(lead.name || lead.purchaser_name || lead.customer_name || '')}
                            </div>
                            <div className="min-w-0">
                              <div className="font-semibold text-gray-900 text-[12px] truncate">{lead.name || lead.purchaser_name || lead.customer_name || '-'}</div>
                              <div className="text-[10px] text-gray-500 mt-0.5">{timeAgo(lead.last_activity_date || lead.updated_at || lead.created_at)}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-2 whitespace-nowrap">
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-1 text-xs text-gray-600">
                              <Mail size={11} className="text-gray-400 flex-shrink-0" />
                              {lead.email ? (
                                <span className="truncate">{lead.email}</span>
                              ) : (
                                <span className="italic text-gray-400">No email</span>
                              )}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-gray-600">
                              <Phone size={11} className="text-gray-400 flex-shrink-0" />
                              {lead.phone || lead.contact_number ? (
                                <span>{userRole === 'admin' ? formatPhoneDisplay(lead.phone || lead.contact_number) : maskPhone(lead.phone || lead.contact_number)}</span>
                              ) : (
                                <span className="italic text-gray-400">No phone</span>
                              )}
                            </div>
                          </div>
                        </td>
                      </>
                    )}
                    {(activeLeadView === 'meta_ads' || activeLeadView === 'general') && (
                    <td className="px-6 py-2 whitespace-nowrap">
                      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-700">
                        {lead.project_name || 'General'}
                      </span>
                    </td>
                    )}
                    {activeLeadView === 'general' && (
                      <>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700">
                          {lead.intent || '-'}
                        </td>
                        <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-700">
                          {formatBudgetDisplay(lead.budget)}
                        </td>
                      </>
                    )}
                    <td className="px-6 py-2 whitespace-nowrap">
                      <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${statusClasses}`}>
                        {formatStatusLabel(lead.status)}
                      </span>
                      {rowRules?.show_priority_chip && (
                        <span className="ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-slate-100 text-slate-600">
                          {(() => {
                            const hrs = Math.max(0, Math.floor((Date.now() - new Date(lead.updated_at || lead.created_at).getTime()) / 3600000));
                            if (String(lead.status || '').toLowerCase() === 'new' && !lead.assigned_to_id) return 'Unassigned';
                            if (hrs >= Number(rowRules.stale_after_hours || 72)) return 'Stale';
                            return 'Active';
                          })()}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap">
                      {(() => {
                        const meta = getLeadScoreMeta(lead.lead_score);
                        return (
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold ${meta.badge}`}>
                            <span className={`inline-block w-1 h-2 rounded-sm ${meta.bars >= 1 ? 'bg-current' : 'bg-current/30'}`} />
                            <span className={`inline-block w-1 h-3 rounded-sm ${meta.bars >= 2 ? 'bg-current' : 'bg-current/30'}`} />
                            <span className={`inline-block w-1 h-4 rounded-sm ${meta.bars >= 3 ? 'bg-current' : 'bg-current/30'}`} />
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap">
                      {renderLastAction(lead)}
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap text-sm text-gray-500">
                      {new Date(lead.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    
                    <td className="px-6 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="h-6 w-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-bold uppercase">
                          {assignedAgentLabel === 'Unassigned' ? 'U' : assignedAgentLabel.charAt(0)}
                        </div>
                        <span className="text-sm text-gray-700 font-medium">
                          {assignedAgentLabel}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-2 whitespace-nowrap">
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${lead.source?.toLowerCase().includes('meta') ? 'bg-purple-50 text-purple-700 border border-purple-100' : 'bg-gray-50 text-gray-700 border border-gray-200'}`}>
                        {lead.source || 'Manual'}
                      </span>
                    </td>

                    <td className="px-6 py-2 whitespace-nowrap text-right">
                      <div className="flex justify-end items-center gap-3">
                        <button
                          onClick={() => handleDeleteLead(lead.id)}
                          className="text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}

              {leads.length === 0 && !loading && (
                <tr>
                  <td colSpan={leadEmptyCols} className="px-6 py-12 text-center bg-white border-t border-gray-100">
                    <UsersIcon className="mx-auto h-12 w-12 text-gray-300" />
                    <h3 className="mt-2 text-sm font-semibold text-gray-900">No leads found</h3>
                    <p className="mt-1 text-sm text-gray-500">Try adjusting your filters or create a new lead.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>

          {/* Pagination */}
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={goToPage} />
        </div>
      </div>

      {/* FLOATING BULK ACTIONS TOOLBAR */}
      {selectedLeads.length > 0 && userRole === 'admin' && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gray-900 text-white px-6 py-3 rounded-xl shadow-2xl flex items-center gap-6 z-40">
          <span className="text-sm font-medium">{selectedLeads.length} leads selected</span>
          <div className="h-4 w-px bg-gray-700"></div>
          <button 
            onClick={() => setShowBulkAssignModal(true)}
            className="text-sm font-medium hover:text-gray-300 transition-colors"
          >
            Assign Agent
          </button>
        </div>
      )}

      {/* BULK ASSIGN MODAL */}
      {showBulkAssignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowBulkAssignModal(false)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Bulk Assign Leads</h3>
            <p className="text-sm text-gray-500 mb-6">Assign {selectedLeads.length} selected leads to a sales agent.</p>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Agent</label>
              <select 
                value={bulkAssignAgent}
                onChange={(e) => setBulkAssignAgent(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900"
              >
                <option value="">-- Choose Agent --</option>
                {assignableUsers.map(u => (
                  <option key={u.id} value={u.id}>{getAgentLabel(u)}</option>
                ))}
              </select>
            </div>

            <div className="flex justify-end gap-3">
              <button onClick={() => setShowBulkAssignModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={handleBulkAssign} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-lg">Confirm Assignment</button>
            </div>
          </div>
        </div>
      )}

      {/* RETAINED MODALS BELOW (Unchanged functionality) */}
      <LeadFormDrawer 
        isOpen={showAddForm} 
        onClose={() => setShowAddForm(false)} 
        onLeadAdded={() => loadLeads(1, searchTerm, filterStatus, filterSource, filterIntent, filterProject)} 
        projects={projects} 
        users={users} 
        userRole={userRole}
        initialView={activeLeadView}
        dynamicConfig={{ propertyTypes, statusOptions, intentOptions, sources }}
      />

      <DataTransferModal
        isOpen={dataTransferState.open}
        initialMode={dataTransferState.mode}
        initialSource={dataTransferState.source}
        onClose={() => setDataTransferState({ open: false, mode: 'import', source: null })}
        onImportSuccess={() => {
          loadLeads(1, searchTerm, filterStatus, filterSource);
          showToast('Leads imported successfully', 'success');
        }}
      />

      {showImportHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowImportHistory(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-heading text-lg font-semibold">Import History</h3>
              <button type="button" onClick={() => setShowImportHistory(false)} className="p-2 hover:bg-neutral-100 rounded"><X size={20} /></button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              {importHistoryLoading ? (
                <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" /></div>
              ) : importHistory.length === 0 ? (
                <p className="text-text-secondary text-sm">No import history yet.</p>
              ) : (
                <ul className="space-y-3">
                  {importHistory.map((row) => (
                    <li key={row.id} className="text-sm p-3 bg-neutral-bg rounded-lg">
                      <span className="font-medium">{row.file_name || 'Import'}</span>
                      <span className="text-text-secondary ml-2">by {row.user_name || 'Unknown'}</span>
                      <p className="text-text-secondary mt-1">{row.imported_count} imported, {row.skipped_count} skipped · {row.total_rows} rows · {new Date(row.created_at).toLocaleString()}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leads;